/**
 * Operational exception card (OS Buddy — issue #1097, epic #1085).
 *
 * Read-only presentation of one operational exception in the foreman queue: type,
 * urgency, status, evidence and the structured resolution options — with their
 * impact for capacity_overbooked. Claim/resolve actions arrive in PR2.
 */
import { useTranslation } from 'react-i18next';
import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import type { ExceptionStatus, ExceptionUrgency, OperationalException } from '@/hooks/useExceptions';
import { EmailDeliveryEvidence } from './EmailDeliveryEvidence';

const URGENCY_COLOR: Record<ExceptionUrgency, 'error' | 'warning' | 'info' | 'default'> = {
  critical: 'error',
  high: 'warning',
  normal: 'info',
  low: 'default',
};

const STATUS_COLOR: Record<ExceptionStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  open: 'warning',
  claimed: 'info',
  released: 'warning',
  reassigned: 'info',
  escalated: 'error',
  stale: 'warning',
  reopened: 'warning',
  resolved: 'success',
  expired: 'default',
  cancelled: 'default',
};

const RISK_COLOR: Record<'low' | 'medium' | 'high', 'success' | 'warning' | 'error'> = {
  low: 'success',
  medium: 'warning',
  high: 'error',
};

function num(evidence: Record<string, unknown> | null, key: string): number | null {
  const v = evidence?.[key];
  return typeof v === 'number' ? v : null;
}

export function ExceptionCard({ exception, onOpen }: { exception: OperationalException; onOpen?: () => void }) {
  const { t, i18n } = useTranslation('exceptions');

  const shortfall = num(exception.evidence, 'shortfallHours');
  const unplaced = num(exception.evidence, 'unplacedCount');
  const decisionDay = typeof exception.evidence?.['decisionDay'] === 'string' ? (exception.evidence['decisionDay'] as string) : null;

  const sla = exception.slaDueAt
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(exception.slaDueAt))
    : null;

  return (
    <Paper
      variant="outlined"
      onClick={onOpen}
      onKeyDown={onOpen ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } } : undefined}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      sx={{
        p: 1.5,
        borderRadius: 2,
        ...(onOpen && { cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }),
      }}
    >
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ flexGrow: 1 }}>
            {t(`type.${exception.type}`, { defaultValue: exception.type })}
          </Typography>
          <Chip size="small" color={URGENCY_COLOR[exception.urgency]} label={t(`urgency.${exception.urgency}`)} />
          <Chip size="small" variant="outlined" color={STATUS_COLOR[exception.status]} label={t(`status.${exception.status}`)} />
        </Box>

        {/* Evidence summary (capacity_overbooked). */}
        {(shortfall != null || unplaced != null || decisionDay) && (
          <Typography variant="body2" color="text.secondary">
            {decisionDay ? t('card.day', { day: decisionDay }) + ' · ' : ''}
            {shortfall != null ? t('card.shortfall', { hours: shortfall }) : ''}
            {shortfall != null && unplaced != null ? ' · ' : ''}
            {unplaced != null ? t('card.unplaced', { count: unplaced }) : ''}
          </Typography>
        )}

        {exception.type === 'email_delivery_failed' && <EmailDeliveryEvidence evidence={exception.evidence} />}

        {sla && <Typography variant="caption" color="text.secondary">{t('card.slaDue', { time: sla })}</Typography>}

        {/* Structured resolution options with impact. */}
        {exception.resolutionOptions && exception.resolutionOptions.length > 0 && (
          <>
            <Divider />
            <Typography variant="caption" color="text.secondary">{t('card.options')}</Typography>
            <Stack spacing={0.75}>
              {exception.resolutionOptions.map((opt) => (
                <Box key={opt.kind} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>{t(`resolution.${opt.kind}`, { defaultValue: opt.label })}</Typography>
                  {opt.impact && opt.impact.overtimeHours > 0 && (
                    <Chip size="small" variant="outlined" label={t('impact.overtime', { hours: opt.impact.overtimeHours })} />
                  )}
                  {opt.impact && (
                    <Chip size="small" color={RISK_COLOR[opt.impact.remainingRisk]} variant="outlined" label={t(`risk.${opt.impact.remainingRisk}`)} />
                  )}
                </Box>
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
}
